import dynamic from "next/dynamic";
import Theme from "/components/Theme";
import SideNavHighlight from "../../components/organisms/common/SideNavHighlight";
import Hero from "../../components/organisms/guides/Hero";
import AppStorage from "../../components/AppStorage";
import {
  viewport,
  colors,
  font,
  lineHeight,
} from "../../styles/cssinjs/helpers/variables.js";
const People = dynamic(() =>
  import("../../components/organisms/common/People")
);
const OutlineCards = dynamic(() =>
  import("@components/organisms/common/OutlineCards")
);
const SignUpCTA = dynamic(() =>
  import("../../components/organisms/common/SignupCTA")
);
const WideCTA = dynamic(() =>
  import("../../components/organisms/common/WideCTA")
);
const FeaturedCard = dynamic(() =>
  import("@components/organisms/guides/FeaturedCard")
);
const TpWidget = dynamic(() => import("@components/atoms/document/TpWidget"));

export async function getStaticPaths({ locales }) {
  // Fetch All Path
  const res = await fetch(`${process.env.JSON_PATH}/routes.json`);
  const data = await res.json();

  // Specific Paths
  let routes = data.guides;

  // Variable for returning path
  let paths = [];

  // Create Paths with locales
  routes.forEach((path) => {
    for (const locale of locales) {
      paths.push({
        params: {
          page: path,
        },
        locale,
      });
    }
  });
  // return to getStaticProps()
  return {
    paths,
    fallback: false,
  };
}
export async function getStaticProps({ params, locale, nonce }) {
  let allData = {};

  // Fetch
  const headerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/header.json`
  );
  const footerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/footer.json`
  );
  const marketingResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/guides/${params.page}/marketing.json`
  );
  const contentResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/guides/${params.page}/content.json`
  );

  // Catch Error
  if (!marketingResponse.ok || !contentResponse.ok) {
    return {
      notFound: true,
    };
  }
  // Convert
  allData.header = await headerResponse.json();
  allData.footer = await footerResponse.json();
  allData.marketing = await marketingResponse.json();
  allData.content = await contentResponse.json();

  // Wrap
  const header = {
    header: allData.header,
  };
  const footer = {
    footer: allData.footer,
  };

  // Combine all data
  const data = await {
    ...header,
    ...footer,
    ...allData.marketing,
    ...allData.content,
  };

  return {
    props: {
      data,
      pageName: params.page,
    },
  };
}

const LeftNavHighlightSection = ({ data, isNewDesign }) => {
  return (
    <section
      id="targett"
      className={`lg:pt-10 ${
        isNewDesign === true
          ? "lg:pb-20 md:pb-5 left-nav bg-light-blue-grey"
          : "lg:pb-10 md:pb-10 md:pt-10"
      }`}
    >
      <div
        className={`container lg:d-fx  pos-rel ${
          isNewDesign === true ? "" : "fx--sb"
        }`}
      >
        <SideNavHighlight data={data} isNewDesign={isNewDesign} />
      </div>
    </section>
  );
};
const OtherGuidesSection = ({ data, isNewDesign }) => {
  return (
    <section
      className={`highlight ${
        isNewDesign === true
          ? "bg-white lg:pt-20 lg:pb-20 md:pt-5 md:pb-5"
          : "bg-light lg:pt-10 lg:pb-15 md:pt-5 md:pb-10"
      }`}
    >
      <article
        className={`container lg:text-center ${
          isNewDesign === true ? "guide-cards md:text-center" : "md:text-left"
        }`}
      >
        <h2
          className="lg:mb-10 md:m-0 md:pb-5 text-xl"
          dangerouslySetInnerHTML={{ __html: data.title }}
        />
        {isNewDesign ? (
          <FeaturedCard
            data={data.cards.list}
            classes="md:m-0 md:w-100 has-four-col"
          />
        ) : (
          <>
            <OutlineCards
              data={data.cards.list}
              classes="md:m-0 md:w-100 has-three-col"
            />
          </>
        )}
      </article>
      <style global jsx>{`
        ${isNewDesign
          ? `
            .guide-cards .has-four-col{
              display: flex;
              justify-content: space-between !important;
              margin: 0
            };
            .guide-cards .highlight-card {
              background-color: ${colors.primitive.white.default} !important;
              border: none !important;
              border-radius: 8px;
              box-shadow: 0 1px 2px 0 rgba(48, 58, 71, 0.2), 0 1px 3px 0 rgba(48, 58, 71, 0.1);
            }
             .guide-cards .highlight-card:hover {
              background-color: ${colors.primitive.white.default} !important;
              box-shadow: 0 2px 4px 0 rgba(48, 58, 71, 0.1), 0 4px 8px 0 rgba(48, 58, 71, 0.14)!important;;
            }
            .guide-cards .highlight-card:hover > p, .guide-cards .highlight-card:hover .icon-title-wrap h3{
              color: ${colors.primitive.blue[600]};
            }
            .guide-cards .gutter-space {
              margin-left: 0px !important;
              margin-right: 0px !important;
            }
            .highlight h2{
              font-size: ${font.size.lg[2.5]};
              line-height: ${lineHeight[12]};
            }
            @media (min-width: ${viewport.tablet.sm}) {
              .guide-cards .highlight-card {
                padding: 10px 26px 32px !important;
                margin-top: 0px !important;
              }
            }
            @media (max-width: ${viewport.tablet.sm}) {
              .highlight h2{
                font-size: ${font.size.lg[1]};
                line-height: ${lineHeight[10]};
              }
              .guide-cards .has-four-col{
                flex-direction: column;
              };
            }
            @media (min-width: ${viewport.tablet.sm}) and (max-width: ${viewport.tablet.md}){
              .highlight{
                padding-top: 80px !important;
                padding-bottom: 40px !important;
              }
              .highlight h2{
                margin-bottom: 40px !important;
              }
              .guide-cards {
                max-width: 600px;
                margin: 0 auto;
              }
              .guide-cards .highlight-card {
                padding: 24px 24px 35px 24px !important;
                margin-top: 0px !important;
                position: relative;
              }
              .guide-cards .highlight-card h3 {
                margin-top: 0px !important;
                margin-bottom: 24px;
                min-height: 90px;
              }
            }
            `
          : ``}
      `}</style>
    </section>
  );
};

export default function Guide({ data, pageName, nonce }) {
  let newDesignPages = [
    "local-seo-tactics",
    "host-a-brand-loyalty-event",
    "best-branding-tools",
    "how-to-generate-more-leads",
    "build-your-brand-on-social-media",
    "how-to-build-brand-loyalty",
    "how-to-protect-your-time",
    "how-to-find-brand-ambassadors",
    "small-business-website",
    "how-to-promote-your-business",
    "instagram-appointment-hacks",
    "building-your-brand-from-scratch",
    "instagram-booking-system",
  ];
  const isNewDesignBool = newDesignPages.includes(pageName);

  const { header, footer, seo, content } = data;

  return (
    <>
      <Theme
        header={header}
        footer={footer}
        className={`page--sub-guides ${
          isNewDesignBool === true ? "" : "faq-root"
        } ${content.helpers.pagename}`}
        seo={seo}
        nonce={nonce}
      >
        <AppStorage pageName={`Guides Sub Page - ${pageName}`} />
        <Hero data={content.hero} isNewDesign={isNewDesignBool} />

        <LeftNavHighlightSection
          data={content.guides}
          isNewDesign={isNewDesignBool}
        />
        {content.hasOwnProperty("otherGuides") && (
          <OtherGuidesSection
            data={content.otherGuides}
            isNewDesign={isNewDesignBool}
          />
        )}
        {isNewDesignBool ? (
          <SignUpCTA data={content.signUpCTA} />
        ) : (
          <>
            <People className="bg-light" />
            <WideCTA data={content.wideCTA} />
          </>
        )}
        {/* Temp Scroll Bug fix for guides page */}
        <style global jsx>{`
          :global(*) {
            scroll-behavior: smooth !important;
          }
          .faq figure svg,
          #easy-share {
            width: 48px;
          }
          .faq-navigation a {
            color: ${colors.primitive.grey[300]};
            transition: color 0.5s;
            font-weight: ${font.weight.bold};
          }
          .faq-navigation a.selected {
            color: ${colors.primitive.black[900]};
          }
          .text-brandcolor-v2 {
            border-bottom: 1px solid ${colors.primitive.black[900]} !important;
          }
          .fw-500 {
            font-weight: 500 !important;
          }
          .fs-georgia {
            font-family: "Georgia", serif !important;
          }
          .l-spacing-0 {
            letter-spacing: 0 !important;
          }
          .text-green {
            color: ${colors.primitive.green.light};
          }
          .underline-green {
            border-bottom: 0.5px solid ${colors.primitive.green.light} !important;
          }
          .text-grey-200 {
            color: ${colors.primitive.grey[200]};
          }
          .underline-grey-800 {
            border-bottom: 1px solid ${colors.primitive.grey[800]} !important;
          }
          .underline-grey {
            border-bottom: 1px solid ${colors.primitive.grey[200]} !important;
          }
          @media (max-width: ${viewport.tablet.sm}) {
            .page--sub-guides .hero--default {
              padding-bottom: 0px !important;
            }
            .page--sub-guides .hero--default.has-align-top figure {
              margin-top: 80px !important;
            }
          }

          @media (min-width: ${viewport.tablet.sm}) and (max-width: ${viewport
              .desktop.md}) {
            ${isNewDesignBool
              ? `
              .page--sub-guides .hero--default {
                width: auto;
                margin-left: auto;
                margin-right: auto;
                padding: 30px 20px 80px 20px !important;
                display: flex;
                flex-direction: column;
                align-items: center;
              } 
              .page--sub-guides .left-nav{
                 padding-top: 0px !important;
                 padding-bottom: 80px !important;
              }
              .page--sub-guides .hero--default article{
                width: 100%;
                max-width: 600px;
              }
              .page--sub-guides .hero--default div {
                margin-top: 45px !important;
                margin-bottom: 12px;
              }
              .page--sub-guides .hero--default.has-align-top figure {
                margin-top: 0px !important;
                width: 100%;
                max-width: 600px;
              }
              .page--sub-guides .hero--default.has-align-top figure img{
                width: auto !important;
              }
              .cta-contrast.has-image.has-img-offset picture{
                display: block;
              }
              .cta-contrast.has-image {
                padding: 0px 0 0px 40px !important;
                max-width: 600px;
                margin: 0 auto;
              }
              .cta-contrast.has-image article {
                max-width: 240px;
                padding-top: 40px;
              }
              .cta-contrast.has-image h2 {
                font-size: ${font.size.md[3]} !important;
                line-height: ${lineHeight[8]} !important;
              }
              `
              : `
              .page--sub-guides .hero--default {
                max-width: 600px;
                margin-left: auto;
                margin-right: auto;
                padding-bottom: 40px !important;
              }
              .page--sub-guides .hero--default > article > h1 {
                padding-top: 0px !important;
                font-size: ${font.size.lg[2]};
                line-height: ${lineHeight[12]};
                margin-bottom: 20px !important;
              }
              .page--sub-guides .hero--default div {
                margin-top: 20px !important;
              }
              .page--sub-guides .btn-primary {
                width: 100%;
              }
              .page--sub-guides .hero--default.has-align-top figure {
                margin-top: 0px !important;
              }
            `}
            .page--sub-guides .hero--default article {
              padding-right: 30px !important;
            }
          }
          ${isNewDesignBool
            ? `
            .page--sub-guides h1, .page--sub-guides h2, .page--sub-guides h3{
              color: ${colors.primitive.black[900]};
            }
            .page--sub-guides p {
              color: ${colors.primitive.grey[800]};
            }
            .page--sub-guides .text-mdx {
              font-size: ${font.size.md[3]};
              line-height: ${lineHeight[8]};
            }
            @media (min-width: ${viewport.desktop.sm}) {
              .cta-section{
                position: relative;
                min-width: 1163px;
                left: -404px;
              }
              .cta-section::before, .cta-section::after{
                content: " ";
                width: 360px;
                height: 80px;
              }
              .cta-section::before{
                position: absolute;
                top: -80px;
                background: linear-gradient(0deg, #EDF0F2 0%, #EDF0F2 22.4%, rgba(237, 240, 242, 0.00) 100%);
              }
              .cta-section::after{
                position: absolute;
                bottom: -80px;
                background: linear-gradient(180deg, #EDF0F2 0%, #EDF0F2 23.44%, rgba(237, 240, 242, 0.00) 100%);
              }
              .cta-section h2{
                font-size: ${font.size.lg[1]} !important;
                line-height: ${lineHeight[10]} !important;
              }
              .cta-section .cta-contrast.has-image {
                padding-left: 100px;
              }
              .cta-section .cta-contrast.has-image a{
                margin-top: 12px !important;
                padding: 12px 8px;
                min-width: 142px;
              }  
            }
            @media (min-width: ${viewport.tablet.sm}) and (max-width: ${viewport.tablet.md}) {
              .cta-section{
                padding-bottom: 0px !important;
              }
            }
            @media (max-width: ${viewport.tablet.sm}) {
              .page--sub-guides .hero--default.has-align-top figure {
                margin-top: 28px !important;
              }
              .page--sub-guides .hero--default {
                padding-bottom: 40px !important;
              }
              .page--sub-guides .cta-section .cta-contrast.has-image{
                padding-top: 20px !important;
                padding-bottom: 20px !important;
                margin: 0px !important;
              }
              .page--sub-guides .text-mdx {
                font-size: ${font.size.md[2]};
                line-height: ${lineHeight[7]};
              }
            }
            `
            : ``}
        `}</style>
        <TpWidget />
      </Theme>
    </>
  );
}
